import { useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { VERANSTALTUNGSSTATUS_BESCHRIFTUNG } from '@mcikm-vote/shared';
import type { Veranstaltung } from '@mcikm-vote/shared';

import {
  useVeranstaltung,
  useVeranstaltungAktion,
  useVeranstaltungLoeschen,
} from '../../admin/veranstaltungenApi';
import type { VeranstaltungAktion } from '../../admin/veranstaltungenApi';
import { BestaetigungsDialog } from '../../components/BestaetigungsDialog';
import { formatiereDatum, formatiereZeitpunkt } from '../../formatierung';

/** Beschreibung der bestätigungspflichtigen Aktionen (§20.3). */
interface AktionsBeschreibung {
  titel: string;
  beschreibung: ReactNode;
  bestaetigungstext: string;
  gefahr?: boolean;
}

function beschreibungFuer(
  aktion: VeranstaltungAktion | 'loeschen',
  v: Veranstaltung,
): AktionsBeschreibung {
  switch (aktion) {
    case 'code':
      return {
        titel: 'Veranstaltungscode neu erzeugen',
        beschreibung: (
          <>
            Der bisherige Code <strong>{v.code}</strong> wird dauerhaft gesperrt und nie erneut
            vergeben. Bereits gedruckte Karten mit diesem Code werden unbrauchbar.
          </>
        ),
        bestaetigungstext: 'Code neu erzeugen',
      };
    case 'freigeben':
      return {
        titel: 'Veranstaltung freigeben',
        beschreibung:
          'Die Teilnehmeranmeldung wird möglich und der Veranstaltungscode unveränderlich. ' +
          'Systemweit darf nur eine Veranstaltung freigegeben oder laufend sein.',
        bestaetigungstext: 'Freigeben',
      };
    case 'beenden':
      return {
        titel: 'Veranstaltung beenden',
        beschreibung:
          'Das ist endgültig. Die Veranstaltung kann danach nicht wieder aktiviert werden, es ' +
          'sind keine weiteren Abstimmungen möglich, und alle Teilnehmersitzungen werden ' +
          'ungültig. Ergebnisse, Exporte und Protokolle bleiben erhalten.',
        bestaetigungstext: 'Endgültig beenden',
        gefahr: true,
      };
    case 'archivieren':
      return {
        titel: 'Veranstaltung archivieren',
        beschreibung:
          'Die Veranstaltung wird aus der normalen Übersicht ausgeblendet. Alle Daten bleiben ' +
          'vollständig erhalten, und die Archivierung lässt sich wieder aufheben.',
        bestaetigungstext: 'Archivieren',
      };
    case 'dearchivieren':
      return {
        titel: 'Archivierung aufheben',
        beschreibung:
          'Die Veranstaltung erscheint wieder in der Übersicht. Sie bleibt beendet und wird ' +
          'nicht reaktiviert.',
        bestaetigungstext: 'Archivierung aufheben',
      };
    case 'warteraum':
      return {
        titel: 'Alle Teilnehmer in den Warteraum setzen',
        beschreibung:
          'Auf allen Teilnehmergeräten erscheint wieder der Warteraum mit dem Hinweis, dass ' +
          'derzeit keine Abstimmung ansteht. Die zuletzt beendete Abstimmung wird dort nicht ' +
          'mehr angezeigt. Ergebnisse, Exporte und Protokolle bleiben unverändert; sobald die ' +
          'nächste Abstimmung geöffnet wird, erscheint sie von selbst.',
        bestaetigungstext: 'In den Warteraum setzen',
      };
    case 'duplizieren':
      return {
        titel: 'Veranstaltung duplizieren',
        beschreibung:
          'Es entsteht ein neuer Entwurf mit denselben Stammdaten und dem Zusatz „Kopie". Die ' +
          'Abstimmungen werden als bearbeitbare Entwürfe übernommen; Code, Tokens, Teilnehmer, ' +
          'Stimmen, Ergebnisse und alle Zeitpunkte nicht.',
        bestaetigungstext: 'Duplizieren',
      };
    case 'loeschen':
      return {
        titel: 'Veranstaltung endgültig löschen',
        beschreibung: (
          <>
            Die Veranstaltung und ihre <strong>{v.tokensGesamt} Token(s)</strong> werden endgültig
            entfernt. Bereits gedruckte Karten werden unbrauchbar. Der Code{' '}
            <strong>{v.code}</strong> bleibt dauerhaft gesperrt und wird nie erneut vergeben. Der
            Protokolleintrag bleibt erhalten.
          </>
        ),
        bestaetigungstext: 'Endgültig löschen',
        gefahr: true,
      };
  }
}

/** Welche Aktionen im jeweiligen Status zulässig sind (§6.2, §6.6, §7.2). */
function verfuegbareAktionen(v: Veranstaltung): (VeranstaltungAktion | 'loeschen')[] {
  switch (v.status) {
    case 'entwurf':
      return ['code', 'freigeben', 'duplizieren', 'loeschen'];
    case 'freigegeben':
    case 'laufend':
      return ['warteraum', 'beenden', 'duplizieren'];
    case 'beendet':
      return ['archivieren', 'duplizieren'];
    case 'archiviert':
      return ['dearchivieren', 'duplizieren'];
  }
}

const BESCHRIFTUNG: Record<VeranstaltungAktion | 'loeschen', string> = {
  code: 'Code neu erzeugen',
  freigeben: 'Freigeben',
  beenden: 'Beenden',
  archivieren: 'Archivieren',
  dearchivieren: 'Archivierung aufheben',
  duplizieren: 'Duplizieren',
  warteraum: 'Alle in den Warteraum',
  loeschen: 'Löschen',
};

/** Einzelansicht einer Veranstaltung mit allen Aktionen aus §6.2, §6.5, §6.6 und §7.2. */
export function VeranstaltungSeite(): ReactElement {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const veranstaltung = useVeranstaltung(id);
  const aktion = useVeranstaltungAktion(id);
  const loeschen = useVeranstaltungLoeschen();
  const [gewaehlt, setGewaehlt] = useState<VeranstaltungAktion | 'loeschen' | null>(null);

  if (veranstaltung.isPending) {
    return <p className="text-text-gedaempft text-sm">Wird geladen …</p>;
  }

  if (veranstaltung.isError) {
    return (
      <p role="alert" className="text-fehler text-sm font-medium">
        {veranstaltung.error.message}
      </p>
    );
  }

  const v = veranstaltung.data;
  const laeuft = aktion.isPending || loeschen.isPending;
  const fehler = aktion.error ?? loeschen.error;

  function fuehreAus(): void {
    if (gewaehlt === null) {
      return;
    }
    if (gewaehlt === 'loeschen') {
      loeschen.mutate(id, {
        onSuccess: () => {
          void navigate('/verwaltung/veranstaltungen');
        },
      });
      return;
    }
    aktion.mutate(gewaehlt, {
      onSuccess: (neue) => {
        setGewaehlt(null);
        if (gewaehlt === 'duplizieren') {
          void navigate(`/verwaltung/veranstaltungen/${neue.id}`);
        }
      },
    });
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">{v.bezeichnung}</h1>
          <p className="text-text-gedaempft text-sm">
            {formatiereDatum(v.veranstaltungsdatum)}
            {v.ort !== null && ` · ${v.ort}`} · {VERANSTALTUNGSSTATUS_BESCHRIFTUNG[v.status]}
          </p>
        </div>
        <Link
          to="../veranstaltungen"
          className="border-rand bg-flaeche-gedaempft min-h-touchflaeche hover:bg-rand flex items-center rounded-md border px-3 text-sm"
        >
          Zur Übersicht
        </Link>
      </div>

      <section className="border-rand bg-flaeche rounded-xl border p-4 shadow-sm">
        <h2 className="mb-3 text-lg font-medium">Veranstaltungscode</h2>
        <p className="font-mono text-2xl tracking-widest">{v.code}</p>
        <p className="text-text-gedaempft mt-2 text-xs">
          Vier Zeichen aus einem Zeichensatz ohne 0, 1, I, L und O. Nach der Freigabe ist der Code
          unveränderlich; ein einmal vergebener Code wird nie erneut verwendet.
        </p>
      </section>

      <section className="border-rand bg-flaeche rounded-xl border p-4 shadow-sm">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-lg font-medium">Tokens</h2>
          <Link to="tokens" className="hover:text-akzent text-sm underline">
            Tokens verwalten
          </Link>
        </div>
        <p className="text-sm">
          {v.tokensGueltig} gültig
          <span className="text-text-gedaempft"> von {v.tokensGesamt} insgesamt erzeugten</span>
        </p>
      </section>

      <section className="border-rand bg-flaeche rounded-xl border p-4 shadow-sm">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-lg font-medium">Abstimmungen</h2>
          <Link to="abstimmungen" className="hover:text-akzent text-sm underline">
            Abstimmungen verwalten
          </Link>
        </div>
        <p className="text-text-gedaempft text-sm">
          Abstimmungen werden als Entwurf vorbereitet, als „bereit" markiert und einzeln geöffnet.
          Je Veranstaltung ist immer nur eine Abstimmung gleichzeitig geöffnet.
        </p>
      </section>

      {(v.beschreibung !== null || v.interneNotiz !== null) && (
        <section className="border-rand bg-flaeche flex flex-col gap-3 rounded-xl border p-4 shadow-sm">
          {v.beschreibung !== null && (
            <div>
              <h2 className="mb-1 text-lg font-medium">Beschreibung</h2>
              <p className="text-sm whitespace-pre-wrap">{v.beschreibung}</p>
            </div>
          )}
          {v.interneNotiz !== null && (
            <div>
              <h2 className="mb-1 text-lg font-medium">Interne Notiz</h2>
              <p className="text-sm whitespace-pre-wrap">{v.interneNotiz}</p>
              <p className="text-text-gedaempft mt-1 text-xs">
                Nur im Administrationsbereich sichtbar.
              </p>
            </div>
          )}
        </section>
      )}

      <section className="border-rand bg-flaeche rounded-xl border p-4 shadow-sm">
        <h2 className="mb-3 text-lg font-medium">Aktionen</h2>

        {fehler !== null && (
          <p role="alert" className="text-fehler mb-3 text-sm font-medium">
            {fehler.message}
          </p>
        )}

        {gewaehlt === null ? (
          <div className="flex flex-wrap gap-2">
            {v.status !== 'beendet' && v.status !== 'archiviert' && (
              <Link
                to="bearbeiten"
                className="border-rand bg-flaeche-gedaempft min-h-touchflaeche hover:bg-rand flex items-center rounded-md border px-4 text-sm font-medium"
              >
                Bearbeiten
              </Link>
            )}
            {verfuegbareAktionen(v).map((name) => (
              <button
                key={name}
                type="button"
                onClick={() => {
                  setGewaehlt(name);
                }}
                className="border-rand bg-flaeche-gedaempft min-h-touchflaeche hover:bg-rand rounded-md border px-4 text-sm font-medium"
              >
                {BESCHRIFTUNG[name]}
              </button>
            ))}
          </div>
        ) : (
          <BestaetigungsDialog
            {...beschreibungFuer(gewaehlt, v)}
            laeuft={laeuft}
            onBestaetigen={fuehreAus}
            onAbbrechen={() => {
              setGewaehlt(null);
            }}
          />
        )}
      </section>

      <section className="border-rand bg-flaeche rounded-xl border p-4 shadow-sm">
        <h2 className="mb-3 text-lg font-medium">Zeitpunkte</h2>
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
          <dt className="text-text-gedaempft">Angelegt</dt>
          <dd>{formatiereZeitpunkt(v.erstelltAm)}</dd>
          <dt className="text-text-gedaempft">Zuletzt geändert</dt>
          <dd>{formatiereZeitpunkt(v.geaendertAm)}</dd>
          {v.freigegebenAm !== null && (
            <>
              <dt className="text-text-gedaempft">Freigegeben</dt>
              <dd>{formatiereZeitpunkt(v.freigegebenAm)}</dd>
            </>
          )}
          {v.beendetAm !== null && (
            <>
              <dt className="text-text-gedaempft">Beendet</dt>
              <dd>{formatiereZeitpunkt(v.beendetAm)}</dd>
            </>
          )}
          {v.archiviertAm !== null && (
            <>
              <dt className="text-text-gedaempft">Archiviert</dt>
              <dd>{formatiereZeitpunkt(v.archiviertAm)}</dd>
            </>
          )}
        </dl>
      </section>
    </div>
  );
}
